import rough from 'roughjs';
/**
 * Warstwa "kreski odręcznej".
 *
 * rough.js generuje kształt LOSOWO przy każdym wywołaniu. Naiwne użycie w Reakcie daje
 * planszę, która drga przy każdym re-renderze i kosztuje setki generacji na klatkę.
 * Dlatego obowiązują tu dwie zasady:
 *
 *  1. Każdy kształt ma deterministyczne ziarno wyprowadzone z pozycji i z id partii.
 *  2. Znaczniki (X-y, kropki) są generowane RAZ jako pula wariantów; pole wybiera wariant
 *     przez hash swoich współrzędnych. Efekt "każdy znaczek inny" przy koszcie bliskim zeru.
 */
const generator = rough.generator();
/** Bok pola w jednostkach viewBox. */
export const CELL = 40;
/** Pas na etykiety A–J / 1–10. */
export const LABEL = 24;
export const boardExtent = (size) => LABEL + size * CELL;
/** Tani, stabilny hash — zamienia współrzędne i identyfikator partii na ziarno kreski. */
export function hashSeed(...parts) {
    let h = 2166136261;
    for (const part of parts) {
        const s = String(part);
        for (let i = 0; i < s.length; i++) {
            h ^= s.charCodeAt(i);
            h = Math.imul(h, 16777619);
        }
    }
    return (h >>> 0) % 2147483647;
}
const BASE = { roughness: 1.2, bowing: 1.5, strokeWidth: 1.6 };
const cache = new Map();
function memo(key, build) {
    const hit = cache.get(key);
    if (hit)
        return hit;
    const value = build();
    cache.set(key, value);
    return value;
}
/** Ramka planszy — jedna kreska na całość, żeby wyglądała jak obrysowana ręcznie. */
export function framePaths(size, seed) {
    return memo(`frame:${size}:${seed}`, () => generator.toPaths(generator.rectangle(LABEL, LABEL, size * CELL, size * CELL, {
        ...BASE,
        seed: seed + 1,
        strokeWidth: 2.2,
    })));
}
/** Linie siatki. Cieńsze i bardziej "ołówkowe" niż ramka. */
export function gridPaths(size, seed) {
    return memo(`grid:${size}:${seed}`, () => {
        const out = [];
        const end = LABEL + size * CELL;
        for (let i = 1; i < size; i++) {
            const at = LABEL + i * CELL;
            out.push(...generator.toPaths(generator.line(at, LABEL, at, end, { ...BASE, seed: seed + i * 7, strokeWidth: 1 })), ...generator.toPaths(generator.line(LABEL, at, end, at, { ...BASE, seed: seed + i * 13, strokeWidth: 1 })));
        }
        return out;
    });
}
/**
 * Obrys kadłuba statku (własna plansza albo odsłonięta flota przeciwnika po partii),
 * z delikatnym kreskowaniem (`hachure`) wewnątrz obrysu — bez tego kadłub to sam kontur
 * i ginie na tle siatki. `filled = false` zostaje samym obrysem: używa tego duch statku
 * w edytorze rozstawiania, gdy pozycja jest legalna (patrz `SketchBoard.tsx`).
 */
export function shipPaths(ship, seed, filled = true) {
    const w = (ship.dir === 'h' ? ship.size : 1) * CELL;
    const h = (ship.dir === 'v' ? ship.size : 1) * CELL;
    return memo(`ship:${ship.id}:${ship.x}:${ship.y}:${ship.dir}:${ship.size}:${seed}:${filled}`, () => generator.toPaths(generator.rectangle(LABEL + ship.x * CELL + 4, LABEL + ship.y * CELL + 4, w - 8, h - 8, {
        ...BASE,
        seed: seed + ship.id * 31,
        fill: filled ? 'currentColor' : undefined,
        fillStyle: 'hachure',
        hachureGap: 6,
        fillWeight: 1.2,
    })));
}
const VARIANTS = 6;
/** Pula wariantów "X" (trafienie) rysowanych w lokalnym układzie pola. */
export const crossVariants = Array.from({ length: VARIANTS }, (_, v) => [
    ...generator.toPaths(generator.line(9, 9, CELL - 9, CELL - 9, { ...BASE, seed: 101 + v * 17, strokeWidth: 2.4 })),
    ...generator.toPaths(generator.line(CELL - 9, 9, 9, CELL - 9, { ...BASE, seed: 211 + v * 23, strokeWidth: 2.4 })),
]);
/** Pula wariantów kropki (pudło). */
export const dotVariants = Array.from({ length: VARIANTS }, (_, v) => generator.toPaths(generator.circle(CELL / 2, CELL / 2, 9, { ...BASE, seed: 307 + v * 29, strokeWidth: 1.6 })));
/**
 * Pula wariantów kółka ołówkiem wokół pola ostatniego ruchu — owal minimalnie większy
 * od pola, jak zakreślenie odręczne, które nie trzyma się linii kratki.
 */
export const ringVariants = Array.from({ length: VARIANTS }, (_, v) => generator.toPaths(generator.ellipse(CELL / 2, CELL / 2, CELL + 4, CELL - 2, {
    ...BASE,
    seed: 401 + v * 37,
    strokeWidth: 1.8,
})));
/** Wariant znaczka dla danego pola — ten sam przy każdym renderze. */
export function variantFor(x, y) {
    return hashSeed(x, y) % VARIANTS;
}
export const columnLabel = (x) => String.fromCharCode(65 + x);
export const rowLabel = (y) => String(y + 1);
//# sourceMappingURL=sketch.js.map